import Reveal from "@/components/site/Reveal";
import { IMAGES, BRAND } from "@/lib/content";
import { MapPin, Phone, Clock } from "lucide-react";

const TIMELINE = [
    {
        year: "1992",
        title: "A single workbench.",
        text: "Hoofs & Hides began as a vision shaped by passion rather than profession. Its founder, Mr. Yamin Ezzat, introduced bespoke shoes, jackets, and a wider range of leather accessories.",
    },
    {
        year: "1999",
        title: "Premium Global Materials.",
        text: "Begins importing premium leather from South Africa, Europe, and Turkey.",
    },
    {
        year: "2009",
        title: "Jackets, tailored.",
        text: "The house expands into leather jackets — bomber, moto, tailored — cut to measurements, no two alike.",
    },
    {
        year: "2020",
        title: "A new chapter.",
        text: "Collaborating with partners and preparing for international expansion.",
    },
    {
        year: "Today",
        title: "Still patient, still here.",
        text: "A quiet workshop, serving three generations of customers. We still stitch by hand. We still answer the phone ourselves.",
    },
];

export default function AboutUs() {
    return (
        <main data-testid="about-page" className="bg-[#0a0a0a] text-white">
            {/* Hero */}
            <section className="relative h-[85svh] min-h-[600px] overflow-hidden">
                <img
                    src={IMAGES.artisan}
                    alt="Artisan portrait"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black" />
                <div className="absolute inset-0 flex items-end">
                    <div className="px-6 sm:px-10 lg:px-16 pb-20 lg:pb-28 w-full">
                        <Reveal>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-6">
                                About · The House
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 className="font-serif text-[15vw] sm:text-[11vw] lg:text-[9vw] font-medium leading-[0.88] tracking-tight max-w-5xl">
                                Thirty years,
                                <br />
                                <em className="font-normal">one</em> obsession.
                            </h1>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Manifesto */}
            <section className="px-6 sm:px-10 lg:px-16 py-28 lg:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                <div className="lg:col-span-5 lg:sticky lg:top-32">
                    <Reveal>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                            Manifesto
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[0.95] tracking-tight">
                            We make
                            <br />
                            few things,
                            <br />
                            <em className="font-normal">carefully</em>.
                        </h2>
                    </Reveal>
                </div>

                <div className="lg:col-span-7 space-y-10 lg:pl-8">
                    <Reveal delay={0.1}>
                        <p className="text-xl lg:text-2xl font-serif text-white/85 leading-relaxed">
                            We believe a good piece of leather is the start of a long
                            friendship. It stretches to your walk, darkens with your
                            weather, carries your stories. So we start slowly —
                            choosing hides in person, drawing patterns in pencil,
                            stitching seams with wax and needle.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="text-base text-white/60 leading-relaxed font-light max-w-2xl">
                            Mr. Yamin Ezzat, an engineer by training, chose to pursue an enduring love for leather craftsmanship—transforming a personal calling into a respected name in bespoke fashion. At a time when custom-made leather goods were rare, the brand stepped in to bridge the gap. Starting with finely crafted shoes and jackets, Hoofs & Hides quickly gained recognition for fulfilling urgent, last-minute orders without ever compromising on quality.
                        </p>
                    </Reveal>

                    <Reveal delay={0.3} y={60}>
                        <div className="relative aspect-[5/4] w-full overflow-hidden">
                            <img
                                src="brand/hnh_four.jpg"
                                alt="Atelier interior"
                                className="w-full h-full object-cover grayscale"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Timeline */}
            <section className="px-6 sm:px-10 lg:px-16 py-24 lg:py-32 border-t border-white/10">
                <Reveal>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                        A short history
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight mb-16 lg:mb-24">
                        Milestones,
                        <br />
                        quietly marked.
                    </h2>
                </Reveal>

                <div className="relative">
                    <div className="absolute left-0 lg:left-32 top-0 bottom-0 w-px bg-white/15" />
                    <ul className="space-y-16 lg:space-y-20">
                        {TIMELINE.map((t, i) => (
                            <Reveal key={t.year} delay={i * 0.06} y={40}>
                                <li
                                    data-testid={`timeline-${t.year.toLowerCase()}`}
                                    className="relative pl-8 lg:pl-48"
                                >
                                    <div className="absolute left-0 lg:left-32 top-2 w-2 h-2 bg-white -translate-x-[3px]" />
                                    <p className="font-mono-custom text-xs tracking-widest text-white/50 mb-3">
                                        {t.year}
                                    </p>
                                    <h3 className="font-serif text-3xl lg:text-4xl font-medium tracking-tight mb-4">
                                        {t.title}
                                    </h3>
                                    <p className="text-base text-white/65 font-light leading-relaxed max-w-2xl">
                                        {t.text}
                                    </p>
                                </li>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Visit Block (Reverse contrast) */}
            <section
                data-testid="about-visit"
                className="bg-[#FAFAFA] text-black px-6 sm:px-10 lg:px-16 py-28 lg:py-40"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 mb-6">
                                Visit us
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight text-black">
                                The door
                                <br />
                                is open.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-10 max-w-lg text-base text-black/60 font-light leading-relaxed">
                                Step in, feel the hides, try on lasts. We're on
                                Wheeler Road — a short walk from Frazer Town's
                                bakeries. We'd rather you see the work than read
                                about it.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 space-y-8">
                        <Reveal delay={0.1}>
                            <InfoRow icon={<MapPin size={16} />} label="Address">
                                <a
                                    href={BRAND.mapUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover-underline"
                                    data-testid="about-map-link"
                                >
                                    {BRAND.address.line1} {BRAND.address.line2}{" "}
                                    {BRAND.address.line3}
                                </a>

                                 <p className="mt-10 max-w-lg text-base text-black/60 font-light leading-relaxed">
                               Coming soon in Assaye Road, Bengaluru.
                            </p>
                               
                            </InfoRow>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <InfoRow icon={<Clock size={16} />} label="Hours">
                                {BRAND.hours.weekdays}
                                <br />
                                {BRAND.hours.sunday}
                            </InfoRow>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <InfoRow icon={<Phone size={16} />} label="Phone">
                                <a
                                    href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                                    className="hover-underline"
                                >
                                    {BRAND.phone}
                                </a>
                            </InfoRow>
                        </Reveal>
                        <Reveal delay={0.4}>
                            <a
                                href="/#appointment"
                                data-testid="about-book-cta"
                                className="group mt-6 inline-flex items-center gap-4 border border-black px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-black hover:text-white transition-all duration-500"
                            >
                                Book your visit
                                <span className="w-6 h-px bg-current transition-all duration-500 group-hover:w-12" />
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>
        </main>
    );
}

function InfoRow({ icon, label, children }) {
    return (
        <div className="border-t border-black/15 pt-6">
            <div className="flex items-center gap-3 mb-3">
                <span className="text-black/60">{icon}</span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-black/50">
                    {label}
                </span>
            </div>
            <p className="text-lg font-serif text-black leading-snug max-w-md">
                {children}
            </p>
        </div>
    );
}
